import React, { useEffect, useState } from 'react';
import DialogModal from '../../../components/DialogModal';
import {
  Box,
  Button,
  CircularProgress,
  Switch,
  Typography
} from '@mui/material';
import DropdownSelect from '../../../components/DropdownSelect';
import {
  academicYears,
  defaultAcademicYear
} from '../../../constants/academicYears';
import { SnackbarSeverityType, UserRole } from '../../../enums';
import { ClassInfo, TeacherProfile } from '../types';
import { useSchoolClassesConfigHook } from '../../common/hooks/useSchoolClassesConfig';
import userApi from '../../../apis/users';
import subjectAndGroupingApis from '../../../apis/subjects';
import { ClassFormWrapper } from '../styled';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

interface EditStaffClassProps {
  staffDetailData: TeacherProfile | undefined;
  open: boolean;
  selectedClassData: ClassInfo | undefined;
  onClose: () => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onEditClassSuccess: () => void;
}

const EditClassForm = ({
  staffDetailData,
  open,
  selectedClassData,
  onClose,
  showToast,
  onEditClassSuccess
}: EditStaffClassProps) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear || '');
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [section, setSection] = useState<string>('');
  const [subject, setSubject] = useState<string[]>(['']);
  const [isClassTeacher, setIsClassTeacher] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [subjectsOptions, setSubjectsOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const { loggedInUser, gradeOptions, divisionOptions, sectionOptions } =
    useSchoolClassesConfigHook();

  useEffect(() => {
    if (selectedClassData && open) {
      setAcademicYear(defaultAcademicYear || '');
      setGrade(selectedClassData.grade || '');
      setDivision(selectedClassData.division || '');
      setSection(selectedClassData.section || '');
      setSubject(selectedClassData.subjects || []);
      setIsClassTeacher(!!selectedClassData.isClassTeacher);
    }
    // eslint-disable-next-line
  }, [selectedClassData, open, defaultAcademicYear]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!loggedInUser?.school || !grade || !division) return;

      setSubjectsLoading(true);
      try {
        const response = await subjectAndGroupingApis.fetchSchoolSubject(
          loggedInUser.school,
          grade,
          division
        );

        const subjectOptions =
          (response as any)?.map((subject: any) => ({
            label: subject.name,
            value: subject.name
          })) || [];

        setSubjectsOptions(subjectOptions);
      } catch (error: any) {
        setSubjectsOptions([]);
        showToast(
          error?.errors?.[0]?.message || 'Failed to fetch subjects',
          SnackbarSeverityType.error
        );
      } finally {
        setSubjectsLoading(false);
      }
    };

    fetchSubjects();
  }, [grade, division, loggedInUser?.school, showToast]);

  const handleModalClose = () => {
    onClose();
    setGrade('');
    setDivision('');
    setSection('');
    setSubject([]);
    setSubjectsOptions([]);
    setIsClassTeacher(false);
    setAcademicYear(defaultAcademicYear || '');
  };

  const handleSubmit = async () => {
    if (!staffDetailData?.id) return;
    setIsLoading(true);
    try {
      const payload = {
        event: 'EDIT_ACADEMIC_YEAR_WISE_MAPPING',
        academicYear,
        grade,
        division,
        subjects: subject,
        section: section,
        isClassTeacher,
        classesToBeDeleted: [],
        activityClubs: staffDetailData?.currentCourseInfo?.activityClubs || []
      };
      await userApi.updateCourseInfo(staffDetailData.id, payload);
      onEditClassSuccess();
      showToast('New class added successfully', SnackbarSeverityType.success);
      handleModalClose();
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message || 'Error adding new class',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const role = staffDetailData?.role || '';

  const isFormValid =
    role === UserRole.SCHOOL_OFFICE ? section : grade && division;
  const disableSubjectDropdown =
    !grade || !division || subjectsLoading || subjectsOptions.length === 0;

  return (
    <DialogModal
      open={open}
      onClose={handleModalClose}
      title='Update Class'
      maxWidth='sm'
      actions={
        <>
          <Button
            onClick={handleModalClose}
            color='secondary'
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color='primary'
            variant='contained'
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Update'
            )}
          </Button>
        </>
      }
    >
      <ClassFormWrapper>
        <DropdownSelect
          label='Academic Year'
          name='academicYear'
          placeholder='Select Academic Year'
          value={academicYear}
          options={academicYears}
          onChange={(event) => setAcademicYear(event.target.value as string)}
          required
          fullWidth
          hasTopLabel
          disabled
        />
        {(role === UserRole.TEACHER || role === UserRole.COORDINATOR) && (
          <>
            <DropdownSelect
              label='Grade'
              placeholder='Select Grade'
              name='grade'
              value={grade}
              options={gradeOptions}
              onChange={(event) => setGrade(event.target.value as string)}
              required
              fullWidth
              hasTopLabel
              disabled
            />
            <DropdownSelect
              label='Division'
              placeholder='Select Division'
              name='division'
              value={division}
              options={divisionOptions}
              onChange={(event) => setDivision(event.target.value as string)}
              required
              fullWidth
              hasTopLabel
              disabled
            />
          </>
        )}
        {role === UserRole.TEACHER && (
          <>
            <SelectAll
              label='Subjects'
              options={subjectsOptions}
              hasTopLabel
              placeholder='Select Subjects'
              onChange={setSubject}
              selected={subject}
              disabled={disableSubjectDropdown}
              formControlMargin='none'
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={isClassTeacher}
                onChange={(e) => setIsClassTeacher(e.target.checked)}
                color='primary'
              />
              <Typography variant='h6'>Class Teachers</Typography>
            </Box>
          </>
        )}
        {role === UserRole.SCHOOL_OFFICE && (
          <DropdownSelect
            label='Section'
            placeholder='Select Section'
            name='section'
            value={section}
            options={sectionOptions}
            onChange={(event) => setSection(event.target.value as string)}
            required
            fullWidth
            hasTopLabel
          />
        )}
      </ClassFormWrapper>
    </DialogModal>
  );
};

export default React.memo(EditClassForm);
